import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Mail, MailX, MailWarning, Loader2, ExternalLink } from "lucide-react";

const STATUS_STYLES = {
  sent:       { label: "Sent",       cls: "bg-blue-50 text-blue-700 border-blue-200",       icon: Mail },
  delivered:  { label: "Delivered",  cls: "bg-green-50 text-green-700 border-green-200",    icon: Mail },
  opened:     { label: "Opened",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Mail },
  clicked:    { label: "Clicked",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Mail },
  skipped:    { label: "Skipped",    cls: "bg-amber-50 text-amber-800 border-amber-200",    icon: MailWarning },
  suppressed: { label: "Suppressed", cls: "bg-amber-50 text-amber-800 border-amber-200",    icon: MailX },
  failed:     { label: "Failed",     cls: "bg-red-50 text-red-700 border-red-200",          icon: MailWarning },
  bounced:    { label: "Bounced",    cls: "bg-red-50 text-red-700 border-red-200",          icon: MailX },
  complained: { label: "Complaint",  cls: "bg-red-50 text-red-700 border-red-200",          icon: MailX },
  queued:     { label: "Queued",     cls: "bg-slate-50 text-slate-700 border-slate-200",    icon: Loader2 },
};

function Badge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.queued;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${s.cls}`}
      data-testid={`email-status-badge-${status}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function CommunicationsTab({ loanId, applicationId, firmId, attorneyEmail }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const params = {};
    if (loanId) params.loan_id = loanId;
    if (applicationId) params.application_id = applicationId;
    if (firmId) params.firm_id = firmId;
    if (attorneyEmail) params.attorney_email = attorneyEmail;
    setLoading(true);
    api.get("/comms/deliveries", { params })
      .then(r => setDeliveries(r.data || []))
      .catch(() => setDeliveries([]))
      .finally(() => setLoading(false));
  }, [loanId, applicationId, firmId, attorneyEmail]);

  if (loading) {
    return <div className="flex items-center gap-2 text-sm text-slate-500 py-8" data-testid="comms-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading communications…
    </div>;
  }

  if (deliveries.length === 0) {
    return (
      <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center bg-slate-50/50" data-testid="comms-empty">
        <Mail className="w-8 h-8 mx-auto text-slate-400 mb-2" />
        <div className="text-sm text-slate-500">No emails sent yet.</div>
        <div className="text-xs text-slate-400 mt-1">All automated + manual communications will appear here.</div>
      </div>
    );
  }

  return (
    <div className="space-y-1" data-testid="communications-tab">
      <div className="grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-2 mb-2 px-2">
        <div>Status</div>
        <div>Subject · Recipient</div>
        <div>Sent by</div>
        <div>When</div>
      </div>
      {deliveries.map(d => (
        <button
          key={d.id}
          onClick={() => setSelected(d)}
          className="w-full grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center rounded-lg px-2 py-2.5 text-left hover:bg-slate-50 transition"
          data-testid={`delivery-row-${d.id}`}
        >
          <Badge status={d.status} />
          <div className="min-w-0">
            <div className="text-sm text-slate-900 font-medium truncate">{d.subject || "(no subject)"}</div>
            <div className="text-xs text-slate-500 truncate">→ {d.recipient}</div>
          </div>
          <div className="text-xs text-slate-500">
            {d.initiated_by === "system" ? <span className="italic">automation</span> : d.initiated_by}
          </div>
          <div className="text-xs text-slate-500 whitespace-nowrap">{formatWhen(d.sent_at || d.created_at)}</div>
        </button>
      ))}

      {selected && <DeliveryDetail delivery={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function DeliveryDetail({ delivery, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-testid="delivery-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1"><Badge status={delivery.status} /></div>
            <h3 className="font-semibold text-slate-900 truncate">{delivery.subject}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none" data-testid="delivery-detail-close">×</button>
        </div>
        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          <Field label="To">{delivery.recipient}</Field>
          {delivery.cc?.length > 0 && <Field label="Cc">{delivery.cc.join(", ")}</Field>}
          {delivery.bcc?.length > 0 && <Field label="Bcc">{delivery.bcc.join(", ")}</Field>}
          <Field label="Template">{delivery.template_key || "—"}</Field>
          <Field label="Trigger">{delivery.event_type || delivery.initiated_via || "—"}</Field>
          <Field label="Initiated by">{delivery.initiated_by}</Field>
          <Field label="Sent">{formatWhen(delivery.sent_at || delivery.created_at)}</Field>
          {delivery.opened_at && <Field label="Opened">{formatWhen(delivery.opened_at)}</Field>}
          {delivery.provider_id && <Field label="Provider ID"><code className="text-xs">{delivery.provider_id}</code></Field>}
          {delivery.error && <Field label="Error"><span className="text-red-600">{delivery.error}</span></Field>}
          {delivery.body_preview && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Preview</div>
              <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap">{delivery.body_preview}</div>
            </div>
          )}
          {delivery.attachments_meta?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Attachments</div>
              <div className="text-xs text-slate-600 space-y-1">
                {delivery.attachments_meta.map((a, i) => (
                  <div key={i} className="flex items-center gap-2"><ExternalLink className="w-3 h-3" />{a.filename}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-sm">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 pt-0.5">{label}</div>
      <div className="text-slate-800 min-w-0 break-words">{children}</div>
    </div>
  );
}
